import * as zExt from "@redotech/rpc/ext";
import * as z from "zod/v4";
import { DiscountValueType } from "../discount";
import {
  BannerStatus,
  TextAppearance,
  TextSize,
  UpsellPageElementType,
  UpsellPageLayoutType,
  UpsellPageProductSource,
} from "./upsell-page-types";

const baseUpsellProductSelectionSchema_ = z.object({
  type: z.literal(UpsellPageElementType.UPSELL_PRODUCT),
  discountAmount: z.string(),
  discountValueType: z.enum(DiscountValueType),
  showDescription: z.boolean().optional(),
  customDescription: z.string().min(1).nullish(),
});

const upsellProductSelectionManualSchema_ =
  baseUpsellProductSelectionSchema_.extend({
    upsellProductSource: z.literal(UpsellPageProductSource.MANUAL),
    /**
     * GID product ID
     */
    productId: z.string(),
    /**
     * GID variant IDs
     */
    variantIds: z.array(z.string()).optional(),
  });
const upsellProductSelectionManualSchema: z.ZodType<UpsellProductSelectionManual> =
  upsellProductSelectionManualSchema_;
export type UpsellProductSelectionManual = z.infer<
  typeof upsellProductSelectionManualSchema_
>;

const upsellProductSelectionCollectionSchema_ =
  baseUpsellProductSelectionSchema_.extend({
    upsellProductSource: z.literal(UpsellPageProductSource.COLLECTION),
    /**
     * non-GID collection ID (don't ask me why)
     */
    collectionId: z.string(),
  });
const upsellProductSelectionCollectionSchema: z.ZodType<UpsellProductSelectionCollection> =
  upsellProductSelectionCollectionSchema_;
export type UpsellProductSelectionCollection = z.infer<
  typeof upsellProductSelectionCollectionSchema_
>;

const upsellProductSelectionShopifyRecommendationSchema_ =
  baseUpsellProductSelectionSchema_.extend({
    upsellProductSource: z.literal(
      UpsellPageProductSource.SHOPIFY_RECOMMENDATION,
    ),
  });
const upsellProductSelectionShopifyRecommendationSchema: z.ZodType<UpsellProductSelectionShopifyRecommendation> =
  upsellProductSelectionShopifyRecommendationSchema_;
export type UpsellProductSelectionShopifyRecommendation = z.infer<
  typeof upsellProductSelectionShopifyRecommendationSchema_
>;

const upsellProductSelectionAiProductRecommendationSchema_ =
  baseUpsellProductSelectionSchema_.extend({
    upsellProductSource: z.literal(UpsellPageProductSource.REDO_AI),
  });
const upsellProductSelectionAiProductRecommendationSchema: z.ZodType<UpsellProductSelectionAiProductRecommendation> =
  upsellProductSelectionAiProductRecommendationSchema_;
export type UpsellProductSelectionAiProductRecommendation = z.infer<
  typeof upsellProductSelectionAiProductRecommendationSchema_
>;

const upsellProductSelectionSchema_ = z.union([
  upsellProductSelectionManualSchema,
  upsellProductSelectionCollectionSchema,
  upsellProductSelectionShopifyRecommendationSchema,
  upsellProductSelectionAiProductRecommendationSchema,
]);
export const upsellProductSelectionSchema: z.ZodType<UpsellPageProductSelection> =
  upsellProductSelectionSchema_;
export type UpsellPageProductSelection = z.infer<
  typeof upsellProductSelectionSchema_
>;

const upsellPageTimerSchema_ = z.object({
  type: z.literal(UpsellPageElementType.TIMER),
  showTimer: z.boolean(),
  backgroundColor: z.string(),
  totalTime: z.string(),
  timerBannerStatus: z.enum(BannerStatus),
  timerTextSize: z.enum(TextSize),
  timerTextColor: z.enum(TextAppearance),
  supportingTextSize: z.enum(TextSize),
  supportingText: z.string(),
});
const upsellPageTimerSchema: z.ZodType<UpsellPageTimer> =
  upsellPageTimerSchema_;
export type UpsellPageTimer = z.infer<typeof upsellPageTimerSchema_>;

const upsellPageBannerSchema_ = z.object({
  type: z.literal(UpsellPageElementType.BANNER),
  showBanner: z.boolean(),
  title: z.string(),
  titleSize: z.enum(TextSize),
  supportingText: z.string(),
  supportingTextSize: z.enum(TextSize),
  backgroundColor: z.string(),
});
const upsellPageBannerSchema: z.ZodType<UpsellPageBanner> =
  upsellPageBannerSchema_;
export type UpsellPageBanner = z.infer<typeof upsellPageBannerSchema_>;

const upsellPageDataDbWriteValidator_ = z.object({
  name: z.string().optional(),
  layout: z.object({
    type: z.enum(UpsellPageLayoutType),
    timer: upsellPageTimerSchema,
    banner: upsellPageBannerSchema,
    upsellProducts: z.array(upsellProductSelectionSchema),
  }),
});
export const upsellPageDataDbWriteValidator: z.ZodType<UpsellPageDataDbWrite> =
  upsellPageDataDbWriteValidator_;
export interface UpsellPageDataDbWrite
  extends z.infer<typeof upsellPageDataDbWriteValidator_> {}

const upsellPageDbWriteValidator_ = z.object({
  upsellPageData: upsellPageDataDbWriteValidator,
});
export const upsellPageDbWriteValidator: z.ZodType<UpsellPageDbWrite> =
  upsellPageDbWriteValidator_;
export interface UpsellPageDbWrite
  extends z.infer<typeof upsellPageDbWriteValidator_> {}

const upsellPageDataDbParser_ = upsellPageDbWriteValidator_.extend({
  createdAt: z.date(),
  updatedAt: z.date(),
  _id: zExt.objectId(),
  teamId: zExt.objectId(),
});
export const upsellPageDataDbParser: z.ZodType<UpsellPageDataDb> =
  upsellPageDataDbParser_;
export interface UpsellPageDataDb
  extends z.infer<typeof upsellPageDataDbParser_> {}
